/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */

import axios from 'axios';
import logger from '../utils/logger';

// Types
export interface UserLeaderboardEntry {
  rank: number;
  username: string;
  userId?: string;
  publicDebates: number;
  publicWins: number;
  winRate: number;
  stats: {
    explanationStrength: number;
    argumentClarity: number;
    rebuttalEffectiveness: number;
    evidenceQuality: number;
    steelmanningRating: number;
  };
  excaliburIndex: number;
}

export interface DebateLeaderboardEntry {
  rank: number;
  id: string;
  topic: string;
  description: string;
  teams: {
    id: string;
    name: string;
    viewpoint: string;
  }[];
  metrics: {
    overallQuality: number;
    explanationQuality: number;
    logicalCoherence: number;
    evidenceQuality: number;
    civilDiscourse: number;
    strongArgumentsTeam1: number;
    strongArgumentsTeam2: number;
    steelmanningRating: number;
  };
  messageCount: number;
  createdAt: string;
  endDate: string;
}

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface LeaderboardResponse<T> {
  leaderboard: T[];
  pagination: PaginationInfo;
  metric: string;
}

// Helper to ensure API path is correct
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Fix API paths to avoid duplicate /api
const makeApiPath = (path: string) => {
  // If API_URL already includes /api, don't add it again
  if (API_URL.endsWith('/api')) {
    return `${API_URL}${path.startsWith('/') ? path : '/' + path}`;
  }
  
  // Otherwise, include /api in the path
  return `${API_URL}/api${path.startsWith('/') ? path : '/' + path}`;
};

/**
 * Get user leaderboard data
 * @param metric - The metric to sort by (wins, winRate, debates, explanationStrength, etc.)
 * @param page - The page number for pagination
 * @param limit - The number of entries per page
 * @returns LeaderboardResponse with user entries
 */
export const getUserLeaderboard = async (
  metric: string = 'wins',
  page: number = 1,
  limit: number = 10
): Promise<LeaderboardResponse<UserLeaderboardEntry>> => {
  try {
    logger.info(`Fetching user leaderboard with params: metric=${metric}, page=${page}, limit=${limit}`);
    
    // Use the original endpoint now that it's fixed
    const response = await axios.get(
      makeApiPath(`/users/leaderboard?metric=${metric}&page=${page}&limit=${limit}`)
    );
    
    logger.info("User leaderboard API response:", response.data);
    
    // Add detailed logging of user objects
    if (response.data && response.data.leaderboard && response.data.leaderboard.length > 0) {
      logger.info("First user object structure:", JSON.stringify(response.data.leaderboard[0], null, 2));
    }
    
    return response.data;
  } catch (error) {
    logger.error('Error fetching user leaderboard:', error);
    
    // Return mock data instead of throwing error
    logger.info('Using client-side mock user leaderboard data due to API error');
    
    // Mock data for demonstration
    const mockUsers: UserLeaderboardEntry[] = [
      {
        rank: 1,
        username: "ClientFallbackUser1",
        publicDebates: 15,
        publicWins: 12,
        winRate: 0.8,
        stats: {
          explanationStrength: 4.5,
          argumentClarity: 4.2,
          rebuttalEffectiveness: 4.8,
          evidenceQuality: 4.3,
          steelmanningRating: 4.6
        },
        excaliburIndex: 4.5
      },
      {
        rank: 2,
        username: "ClientFallbackUser2",
        publicDebates: 10,
        publicWins: 7,
        winRate: 0.7,
        stats: {
          explanationStrength: 4.2,
          argumentClarity: 4.5,
          rebuttalEffectiveness: 3.9,
          evidenceQuality: 4.1,
          steelmanningRating: 4.3
        },
        excaliburIndex: 4.2
      },
      {
        rank: 3,
        username: "ClientFallbackUser3",
        publicDebates: 8,
        publicWins: 5,
        winRate: 0.625,
        stats: {
          explanationStrength: 3.8,
          argumentClarity: 4.0,
          rebuttalEffectiveness: 4.2,
          evidenceQuality: 3.7,
          steelmanningRating: 4.0
        },
        excaliburIndex: 4.0
      }
    ];
    
    return {
      leaderboard: mockUsers,
      pagination: {
        total: 3,
        page: page,
        limit: limit,
        pages: 1
      },
      metric
    };
  }
};

/**
 * Get debate leaderboard data
 * @param metric - The metric to sort by (quality, explanationQuality, logicalCoherence, etc.)
 * @param page - The page number for pagination
 * @param limit - The number of entries per page
 * @returns LeaderboardResponse with debate entries
 */
export const getDebateLeaderboard = async (
  metric: string = 'quality',
  page: number = 1,
  limit: number = 10
): Promise<LeaderboardResponse<DebateLeaderboardEntry>> => {
  try {
    logger.info(`Fetching debate leaderboard with params: metric=${metric}, page=${page}, limit=${limit}`);
    
    const response = await axios.get(
      makeApiPath(`/debates/leaderboard?metric=${metric}&page=${page}&limit=${limit}`)
    );
    
    logger.info("Debate leaderboard API response:", response.data);
    return response.data;
  } catch (error) {
    logger.error('Error fetching debate leaderboard:', error);
    
    // Return mock data instead of throwing error
    logger.info('Using client-side mock debate leaderboard data due to API error');
    
    // Mock data for demonstration
    const mockDebates: DebateLeaderboardEntry[] = [
      {
        rank: 1,
        id: "mock-debate-1",
        topic: "Should AI systems have rights?",
        description: "A debate on the ethical and legal status of artificial intelligence systems.",
        teams: [
          { id: "team1", name: "Team Affirmative", viewpoint: "AI systems should have limited legal rights" },
          { id: "team2", name: "Team Negative", viewpoint: "AI systems should not have rights" }
        ],
        metrics: {
          overallQuality: 4.7,
          explanationQuality: 4.8,
          logicalCoherence: 4.6,
          evidenceQuality: 4.5,
          civilDiscourse: 5.0,
          strongArgumentsTeam1: 12,
          strongArgumentsTeam2: 10,
          steelmanningRating: 4.7
        },
        messageCount: 48,
        createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        rank: 2,
        id: "mock-debate-2",
        topic: "Is cryptocurrency the future of money?",
        description: "Examining the potential of cryptocurrencies to replace traditional fiat currencies.",
        teams: [
          { id: "team3", name: "Crypto Advocates", viewpoint: "Cryptocurrencies will eventually replace fiat currencies" },
          { id: "team4", name: "Traditional Finance", viewpoint: "Cryptocurrencies will remain niche investments" }
        ],
        metrics: {
          overallQuality: 4.5,
          explanationQuality: 4.6,
          logicalCoherence: 4.3,
          evidenceQuality: 4.7,
          civilDiscourse: 4.8,
          strongArgumentsTeam1: 9,
          strongArgumentsTeam2: 8,
          steelmanningRating: 4.4
        },
        messageCount: 36,
        createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        rank: 3,
        id: "mock-debate-3",
        topic: "Should remote work become the standard?",
        description: "Exploring the benefits and challenges of remote work as the default employment model.",
        teams: [
          { id: "team5", name: "Remote Advocates", viewpoint: "Remote work should be the default option" },
          { id: "team6", name: "Office Proponents", viewpoint: "In-person work offers irreplaceable benefits" }
        ],
        metrics: {
          overallQuality: 4.2,
          explanationQuality: 4.3,
          logicalCoherence: 4.1,
          evidenceQuality: 4.0,
          civilDiscourse: 4.9,
          strongArgumentsTeam1: 7,
          strongArgumentsTeam2: 7,
          steelmanningRating: 4.2
        },
        messageCount: 42,
        createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: new Date(Date.now() - 27 * 24 * 60 * 60 * 1000).toISOString()
      }
    ];
    
    return {
      leaderboard: mockDebates,
      pagination: {
        total: 3,
        page: page,
        limit: limit,
        pages: 1
      },
      metric
    };
  }
}; 